"use client";

import { Anchor, Box } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useEffect, useRef, useState } from "react";
import { FiExternalLink } from "react-icons/fi";

import { Button, Modal, Text } from "@/components/ui/elements";

import {
  buildWorkIntentionSurveyEmbedUrl,
  buildWorkIntentionSurveyExternalUrl,
  WORK_INTENTION_SURVEY_COPY,
  WORK_INTENTION_SURVEY_IFRAME_LOAD_TIMEOUT_MS,
} from "./_function/workIntentionSurveyForm";

type Props = {
  opened: boolean;
  workerId: string | null | undefined;
  onClose: () => void;
};

export function WorkIntentionSurveyModal({ opened, workerId, onClose }: Props) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [loadFailed, setLoadFailed] = useState(false);
  const embedUrl = buildWorkIntentionSurveyEmbedUrl(workerId);
  const externalUrl = buildWorkIntentionSurveyExternalUrl(workerId);
  const loadTimeoutIdRef = useRef<number | null>(null);

  const clearLoadTimeout = () => {
    if (loadTimeoutIdRef.current !== null) {
      window.clearTimeout(loadTimeoutIdRef.current);
      loadTimeoutIdRef.current = null;
    }
  };

  useEffect(() => {
    if (!opened) {
      clearLoadTimeout();
      setLoadFailed(false);
      return;
    }

    setLoadFailed(false);
    loadTimeoutIdRef.current = window.setTimeout(() => {
      loadTimeoutIdRef.current = null;
      setLoadFailed(true);
    }, WORK_INTENTION_SURVEY_IFRAME_LOAD_TIMEOUT_MS);

    return clearLoadTimeout;
  }, [opened, embedUrl]);

  const handleIframeLoad = () => {
    clearLoadTimeout();
    setLoadFailed(false);
  };

  const modalHeight = isMobile ? "92dvh" : "min(90dvh, 800px)";

  return (
    <Modal
      opened={opened}
      close={onClose}
      size="lg"
      padding={0}
      withCloseButton={false}
      closeOnClickOutside={false}
      zIndex={450}
      styles={{
        inner: {
          padding: isMobile ? 8 : undefined,
        },
        content: {
          borderRadius: 16,
          overflow: "hidden",
          ...(isMobile
            ? {
                width: "calc(100vw - 16px)",
                maxWidth: "calc(100vw - 16px)",
              }
            : {}),
          height: modalHeight,
          maxHeight: modalHeight,
          display: "flex",
          flexDirection: "column",
        },
        body: {
          padding: 0,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        },
      }}
    >
      <Box p="md" style={{ borderBottom: "1px solid #e9ecef", flexShrink: 0 }}>
        <Text fz="md" fw={700}>
          {WORK_INTENTION_SURVEY_COPY.title}
        </Text>
        <Text fz="sm" c="dimmed" mt={4}>
          {WORK_INTENTION_SURVEY_COPY.description}
        </Text>
      </Box>

      <Box
        style={{
          flex: 1,
          minHeight: 0,
          overflow: loadFailed ? "auto" : "hidden",
        }}
      >
        {loadFailed ? (
          <Box p="md">
            <Text fz="sm" c="dimmed">
              {WORK_INTENTION_SURVEY_COPY.loadError}
            </Text>
            <Anchor
              href={externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              mt="sm"
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <FiExternalLink size={14} aria-hidden />
              {WORK_INTENTION_SURVEY_COPY.openInBrowser}
            </Anchor>
          </Box>
        ) : (
          <Box
            component="iframe"
            src={embedUrl}
            title={WORK_INTENTION_SURVEY_COPY.title}
            onLoad={handleIframeLoad}
            style={{
              width: "100%",
              height: "100%",
              border: 0,
              display: "block",
            }}
          />
        )}
      </Box>

      <Box
        p="md"
        style={{
          borderTop: "1px solid #e9ecef",
          display: "flex",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Button variant="outline" onClick={onClose}>
          {WORK_INTENTION_SURVEY_COPY.closeButton}
        </Button>
      </Box>
    </Modal>
  );
}
